'use client';

import { OrderTriggerCondition, PositionDirection } from '@drift-labs/sdk';
import {
	matchEnum,
	COMMON_UI_UTILS,
	UIMarket,
	lastOrderStatusToNormalEng,
	isEntirePositionOrder,
} from '@drift/common';
import { useState } from 'react';
import { cm } from 'src/utils/classNames';
import MobileTableCard from '../../Tables/cards/MobileTableCard';
import TableV2 from '../../Tables/TableV2';
import { Swap } from '@drift-labs/icons';
import { OrderRowCollapsedContentMobile } from './OrderRowCollapsedContent';
import { ParentOrderInfo } from 'src/@types/historyTables';
import Tag from 'src/components/primitives/Tag/Tag';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { AmountDisplayType } from './TradePageOrderHistoryTable';

interface TradePageOrderHistoryMobileCardProps {
	orderInfo: ParentOrderInfo;
	displayDatesAsTimestamp: boolean;
	toggleDateDisplayType: () => void;
	amountDisplayType: AmountDisplayType;
	onToggleAmountDisplayType: () => void;
	orderedQuoteValue: string | null;
}

export function TradePageOrderHistoryMobileCard({
	orderInfo,
	displayDatesAsTimestamp,
	toggleDateDisplayType,
	amountDisplayType,
	onToggleAmountDisplayType,
	orderedQuoteValue,
}: TradePageOrderHistoryMobileCardProps) {
	const [expanded, setExpanded] = useState(false);

	const uiMarket = new UIMarket(orderInfo.marketIndex, orderInfo.marketType);
	const marketName = uiMarket.uiSymbols.baseAssetDisplaySymbol;
	const orderStatus = lastOrderStatusToNormalEng(orderInfo.lastActionStatus);
	const isLong = matchEnum(orderInfo.direction, PositionDirection.LONG);

	const avgPriceBn = COMMON_UI_UTILS.calculateAverageEntryPrice(
		orderInfo.quoteAssetAmountFilled,
		orderInfo.baseAssetAmountFilled
	);

	const orderTypeLabel =
		COMMON_UI_UTILS.getOrderLabelFromOrderDetails(orderInfo);
	const displayOrderTypeLabel =
		orderTypeLabel === 'Oracle Market' ? 'Market' : orderTypeLabel;

	const isEntirePosition = isEntirePositionOrder(orderInfo.baseAssetAmount);

	const orderedSizeText = isEntirePosition
		? 'Entire Position'
		: amountDisplayType === 'base'
			? orderInfo.baseAssetAmount.prettyPrint()
			: orderInfo.price.eqZero()
				? 'Market'
				: orderInfo.baseAssetAmount
						.mul(orderInfo.price)
						.shiftTo(orderInfo.price.precision)
						.toNotional();

	const filledSizeText =
		amountDisplayType === 'base'
			? orderInfo.baseAssetAmountFilled.prettyPrint()
			: orderInfo.quoteAssetAmountFilled.toNotional();

	const triggerPriceText = orderInfo.triggerPrice.eqZero()
		? '-'
		: `${
				matchEnum(orderInfo.triggerCondition, OrderTriggerCondition.ABOVE) ||
				matchEnum(
					orderInfo.triggerCondition,
					OrderTriggerCondition.TRIGGERED_ABOVE
				)
					? '>'
					: '<'
			} ${orderInfo.triggerPrice.toNotional(true)}`;

	return (
		<MobileTableCard.Root expanded={expanded} setExpanded={setExpanded}>
			<MobileTableCard.CollapsibleHeader expanded={expanded}>
				<div className="flex w-full items-center justify-between">
					<div className="flex flex-col gap-y-xs">
						<div className="flex items-center">
							<span className="typo-t2 text-text-default">{marketName}</span>
						</div>
						<div className="flex items-center gap-x-xs">
							<Tag
								className={
									isLong
										? 'bg-text-positive-green-button/20 text-text-positive-green-button'
										: 'bg-text-negative-red-button/20 text-text-negative-red-button'
								}
							>
								{uiMarket.isPerp && !uiMarket.isPredictionMarket
									? orderInfo.reduceOnly
										? `Close ${isLong ? 'Short' : 'Long'}`
										: isLong
											? 'Long'
											: 'Short'
									: isLong
										? 'Buy'
										: 'Sell'}
							</Tag>
							<Tag className="bg-button-secondary-bg text-text-default">
								{displayOrderTypeLabel}
							</Tag>
						</div>
					</div>
					<div className="flex flex-col items-end gap-y-xs">
						<div
							className={cm(
								'flex flex-col items-end gap-y-xs transition-all duration-300',
								!expanded ? 'opacity-100' : 'opacity-0'
							)}
						>
							<span className="flex items-center gap-x-xs">
								<MobileTableCard.Label>
									{amountDisplayType === 'base' ? 'Order Size' : 'Order Value'}
								</MobileTableCard.Label>
								{!isEntirePosition && (
									<IconButton
										size="sm"
										variant="tertiary"
										className="text-text-tertiary"
										onClick={(e) => {
											e.stopPropagation();
											onToggleAmountDisplayType();
										}}
										icon={Swap}
									/>
								)}
							</span>
							<span className="typo-t2 whitespace-nowrap">
								{orderedSizeText}
							</span>
						</div>
					</div>
				</div>
			</MobileTableCard.CollapsibleHeader>
			<MobileTableCard.CollapsibleContent>
				<div className="flex flex-col gap-y-lg">
					<div className="grid w-full grid-cols-2 gap-lg">
						<div className="flex flex-col gap-y-xs">
							<span className="flex items-center gap-x-xs">
								<MobileTableCard.Label>
									{amountDisplayType === 'base' ? 'Order Size' : 'Order Value'}
								</MobileTableCard.Label>
								{!isEntirePosition && (
									<IconButton
										size="sm"
										variant="tertiary"
										className="text-text-tertiary"
										onClick={onToggleAmountDisplayType}
										icon={Swap}
									/>
								)}
							</span>
							<span className="typo-t2">{orderedSizeText}</span>
						</div>
						<div className="flex flex-col items-end gap-y-xs">
							<span className="flex items-center gap-x-xs">
								<MobileTableCard.Label>
									{amountDisplayType === 'base'
										? 'Filled Size'
										: 'Filled Value'}
								</MobileTableCard.Label>
								<IconButton
									size="sm"
									variant="tertiary"
									className="text-text-tertiary"
									onClick={onToggleAmountDisplayType}
									icon={Swap}
								/>
							</span>
							<span className="typo-t2">{filledSizeText}</span>
						</div>
					</div>
					<div className="grid grid-cols-3 gap-lg">
						<div className="flex flex-col gap-y-xs">
							<MobileTableCard.Label>Avg Fill Price</MobileTableCard.Label>
							<span className="typo-t4">
								{orderStatus !== 'Open' ? avgPriceBn.toNotional(true) : '-'}
							</span>
						</div>
						<div className="flex flex-col items-start gap-y-xs">
							<MobileTableCard.Label>Fee</MobileTableCard.Label>
							<span className="typo-t4">
								{orderInfo.cumulativeFee?.eqZero()
									? '-'
									: orderInfo.cumulativeFee?.toNotional()}
							</span>
						</div>
						<div className="flex flex-col items-end gap-y-xs">
							<MobileTableCard.Label>Trigger Condition</MobileTableCard.Label>
							<span className="typo-t4">{triggerPriceText}</span>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-lg">
						<div className="flex flex-col gap-y-xs">
							<MobileTableCard.Label>Flag</MobileTableCard.Label>
							<span className="typo-t4">
								{orderInfo.postOnly && orderInfo.reduceOnly
									? 'Post & Reduce only'
									: orderInfo.postOnly
										? 'Post only'
										: orderInfo.reduceOnly
											? 'Reduce only'
											: '-'}
							</span>
						</div>
						<div className="flex flex-col items-end gap-y-xs">
							<MobileTableCard.Label>Status</MobileTableCard.Label>
							<span className="typo-t3">{orderStatus}</span>
						</div>
					</div>
					<div className="flex w-fit items-center gap-x-xs">
						<MobileTableCard.Label>Time</MobileTableCard.Label>
						<TableV2.DateCell
							date={new Date(orderInfo.ts.toNumber() * 1000)}
							displayAsTimestamp={displayDatesAsTimestamp}
							className="py-0 typo-t4 whitespace-nowrap"
						/>
						<IconButton
							size="sm"
							variant="tertiary"
							className="text-text-tertiary"
							onClick={toggleDateDisplayType}
							icon={Swap}
						/>
					</div>
					{expanded && (
						<OrderRowCollapsedContentMobile
							orderId={orderInfo.orderId}
							displayDatesAsTimestamp={displayDatesAsTimestamp}
							expanded={expanded}
							toggleDateDisplayType={toggleDateDisplayType}
							amountDisplayType={amountDisplayType}
							orderedQuoteValue={orderedQuoteValue}
						/>
					)}
				</div>
			</MobileTableCard.CollapsibleContent>
		</MobileTableCard.Root>
	);
}
